class DealersApi {

  /**
   * @type {AbortController | null}
   */
  #abortController = null;

  #getQueryParameters(options = {}) {
    const queryParameters = {};

    if (options.fromAddress) {
      queryParameters.fromAddress = true;
    }

    if (options?.activeFilters?.length) {
      queryParameters.ct = options.activeFilters.sort().join(',');
    }

    if(options?.activeServiceFilters?.length) {
      queryParameters.services = options.activeServiceFilters.sort().join(',');
    }

    // Add detailed location as params, url path contains more inaccurate coordinates
    if (options.location) {
      queryParameters.lat = options.location.lat;
      queryParameters.lon = options.location.lng;
    }

    // Fetch only dealers that have services
    if(options.includeServices) {
      queryParameters.withServices = true;
    }

    const apiParams = window.ntParams || {};
    if (apiParams.nht === true) {
      queryParameters.heavy = true;
    }
    if (apiParams.cityMapSlug) {
      queryParameters.city = apiParams.cityMapSlug;
    }

    // Get availability for buying options product, if selected
    if (options.productCode) {
      queryParameters.productCode = options.productCode;
    }

    return queryParameters;
  }

  async #fetchDealers(url) {
    if (this.#abortController) {
      this.#abortController.abort('REQUEST_CANCELED');
    }

    try {
      this.#abortController = new AbortController();
      const response = await fetch(window.ntApi + url, {
        method: 'GET',
        headers: {
          'X-Requested-With': 'xhr',
        },
        signal: this.#abortController.signal,
      });
      this.#abortController = null;
      const body = await response.json();
      if (response.status === 200 && body?.result?.dealers) {
        const dealers = body.result.dealers.map(dealer => processDealer(dealer));
        return dealers;
      }
      else {
        throw new Error('Dealer data not available.');
      }
    }
    catch(e) {
      this.#abortController = null;
      throw e;
    }
  }

  /**
   * Get dealers that are closest to given location.
   *
   * Returned dealer amount is dependent on requested country (usually 25 to 50).
   *
   * @param {object} location Internal geocode result.
   * @param {object} [options] Query parameters.
   * @return {Promise<object[]>} Array of dealer objects.
   */
  async getDealersByLocation(location, options = {}) {
    const lat = location.lat.toString().substring(0, 5);
    const lng = location.lng.toString().substring(0, 6);
    const countryCode = location.countryCode.toLowerCase();
    const params = this.#getQueryParameters({
      ...options,
      location,
    });

    const query = new URLSearchParams(params).toString();
    const url = `dealers/nearest/latlon/${lat}/${lng}/${countryCode}/`;

    const result = await this.#fetchDealers(url + (query ? '?' + query : ''));
    return result;
  }

  /**
   * Get dealers that are in provided area.
   *
   * Note! Backend has a country specific limit for returned dealer amounts (usually between 50 to 200).
   * Requesting large area will likely hit that limit.
   *
   * @param {google.maps.LatLngBoundsLiteral} bounds Google Maps boundary object.
   * @param {object} [options] Query parameters.
   * @returns {Promise<object[]>} Array of dealer objects.
   */
  async getDealersByBounds(bounds, options = {}) {
    const swLat = bounds.south.toString().substring(0, 5);
    const swLon = bounds.west.toString().substring(0, 5);
    const neLat = bounds.north.toString().substring(0, 5);
    const neLon = bounds.east.toString().substring(0, 5);

    const params = this.#getQueryParameters(options);

    const query = new URLSearchParams(params).toString();
    const url = `dealers/rect/${options.countryCode.toLowerCase()}/${swLat}/${swLon}/${neLat}/${neLon}/`;

    const result = await this.#fetchDealers(url + (query ? '?' + query : ''));
    return result;
  }

}

export default new DealersApi();

export function processDealer(dealer) {
  dealer.company_types = dealer.company_type.map(
    v => window.ntCompanyTypes.find(ct => ct.id === v)
  ).filter(v => !!v);

  dealer.services_data = dealer.services.map(
    v => window.ntServiceFilters.find(sf => sf.id === v)
  ).filter(v => !!v);

  const markers = dealer.company_types.sort((a, b) => {
    return a.marker_priority > b.marker_priority ? -1 : 1;
  });
  const marker = markers.length > 0 ? markers[0] : window.ntCompanyTypes.find(ct => ct.id === 2);
  dealer.marker = {
    inactive: marker.inactive_marker,
    inactiveIndex: marker.inactive_marker_index,
    active: marker.active_marker,
    activeIndex: marker.active_marker_index,
  };

  return dealer;
}
